const registeredModules = {};
const { set } = require('lodash');
const merge = require('lodash/merge');
const uniq = require('lodash/uniq');
const TYPEBOX_OPTIONAL_SYMBOL = Symbol.for('TypeBox.Optional');

/**
 * Registers an HNode type module in the global registry.
 * @param {string} hNodeTypeGroup - The group/category of the HNode type
 * @param {string} hNodeType - The specific HNode type name
 * @param {object} module - The module to register
 */
function registerHNodeType(hNodeTypeGroup, hNodeType, module) {
    set(registeredModules, [hNodeTypeGroup, hNodeType], module);
}

/**
 * Retrieves all HNode types within a group.
 * @param {string} hNodeTypeGroup - The group to retrieve
 * @returns {object|undefined} All registered types in the group
 */
function getHNodeTypeGroup(hNodeTypeGroup) {
    return registeredModules[hNodeTypeGroup];
}

/**
 * Retrieves a specific HNode type module.
 * @param {string} hNodeTypeGroup - The group containing the type
 * @param {string} hNodeType - The specific type to retrieve
 * @returns {object|undefined} The registered module
 */
function getHNodeType(hNodeTypeGroup, hNodeType) {
    return registeredModules[hNodeTypeGroup]?.[hNodeType];
}

/**
 * Normalizes action values to a consistent array format.
 * Handles undefined, single values, and arrays.
 * @param {string|string[]|undefined} value - Action value(s) to normalize
 * @returns {string[]} Array of action strings (empty if no valid input)
 */
function normalizeActions(value) {
    if (!value) return [];
    return Array.isArray(value) ? value.filter(Boolean) : [value];
}

/**
 * Determines whether a property is required in all canonical write contexts.
 * Canonical contexts are create and update.
 *
 * @param {string} fieldName - Property key
 * @param {object} schema - Property schema with x-* metadata
 * @returns {boolean} True when property should be in relation required[]
 */
function isRequiredForAllCanonicalContexts(fieldName, schema) {
    if (!schema || typeof schema !== 'object') return false;
    // Foreign key fields (namespace:relation) should never be universally required
    if (fieldName.includes(':')) return false;
    if (!schema['x-required']) return false;

    const requiredActions = normalizeActions(schema['x-requiredActions']);
    const actionScope = requiredActions.length ? requiredActions : normalizeActions(schema['x-forAction']);

    // No action scope means required globally
    if (!actionScope.length) return true;
    if (actionScope.includes('*')) return true;

    return actionScope.includes('create') && actionScope.includes('update');
}

/**
 * Copies enumerable symbol-backed descriptors from source schemas to the merged schema.
 * TypeBox optional metadata is stored on symbols, and lodash merge does not preserve them.
 */
function copyEnumerableSymbolDescriptors(target, ...sources) {
    (sources || []).forEach(source => {
        if (!source || typeof source !== 'object') return;
        Object.getOwnPropertySymbols(source).forEach(symbol => {
            const descriptor = Object.getOwnPropertyDescriptor(source, symbol);
            if (!descriptor?.enumerable) return;
            Object.defineProperty(target, symbol, descriptor);
        });
    });
}

/**
 * Ensures the TypeBox optional symbol matches resolved x-optional state after merge.
 */
function normalizeTypeBoxOptionalSymbol(merged, existing = {}, incoming = {}) {
    if (!merged || typeof merged !== 'object') return;

    const shouldBeOptional = merged['x-optional'] === true;
    if (!shouldBeOptional) {
        if (Object.prototype.hasOwnProperty.call(merged, TYPEBOX_OPTIONAL_SYMBOL)) {
            delete merged[TYPEBOX_OPTIONAL_SYMBOL];
        }
        return;
    }

    const incomingDescriptor = Object.getOwnPropertyDescriptor(incoming, TYPEBOX_OPTIONAL_SYMBOL);
    const existingDescriptor = Object.getOwnPropertyDescriptor(existing, TYPEBOX_OPTIONAL_SYMBOL);
    const preferredDescriptor = incomingDescriptor || existingDescriptor;

    Object.defineProperty(merged, TYPEBOX_OPTIONAL_SYMBOL, {
        value: preferredDescriptor?.value ?? 'Optional',
        enumerable: true,
        configurable: preferredDescriptor?.configurable ?? true,
        writable: preferredDescriptor?.writable ?? true
    });
}

/**
 * Merges schema flags (x-forAction, x-required, x-optional, x-requiredActions)
 * from two schemas with intelligent conflict resolution.
 *
 * Key behaviors:
 * - x-forAction arrays are combined (union)
 * - x-required is true if either schema requires it
 * - x-optional defaults to true unless field is required for all contexts
 *
 * @param {object} existing - Existing schema with flags
 * @param {object} incoming - New schema to merge in
 * @returns {object} Merged schema with resolved flags
 */
function mergeSchemaFlags(existing = {}, incoming = {}) {
    const merged = merge({}, existing, incoming);
    copyEnumerableSymbolDescriptors(merged, existing, incoming);

    // Combine x-forAction arrays from both schemas
    const existingForActions = normalizeActions(existing['x-forAction']);
    const incomingForActions = normalizeActions(incoming['x-forAction']);
    const allForActions = uniq([...existingForActions, ...incomingForActions]);
    if (allForActions.length) {
        merged['x-forAction'] = allForActions;
    }

    // Build set of actions where field is required
    const requiredActions = new Set(normalizeActions(existing['x-requiredActions']));
    normalizeActions(incoming['x-requiredActions']).forEach(action => requiredActions.add(action));

    // Infer required actions from x-required + x-forAction combination
    function addRequiredFromSchema(schema) {
        if (!schema || !schema['x-required']) return;
        const actions = normalizeActions(schema['x-forAction']);
        if (actions.length) {
            actions.forEach(action => requiredActions.add(action));
        } else {
            // No action specified = required globally
            requiredActions.add('*');
        }
    }

    addRequiredFromSchema(existing);
    addRequiredFromSchema(incoming);

    if (requiredActions.size) {
        merged['x-requiredActions'] = Array.from(requiredActions);
    }

    // Determine if field is required in all action contexts
    const hasGlobalRequired = requiredActions.has('*');
    const hasCreate = requiredActions.has('create');
    const hasUpdate = requiredActions.has('update');
    const requiresAllContexts =
        hasGlobalRequired ||
        (hasCreate && hasUpdate) ||
        (!requiredActions.size && (!!existing['x-required'] || !!incoming['x-required']));

    // x-optional: false only when required in ALL contexts
    const optionalFlag = incoming['x-optional'] ?? existing['x-optional'];
    merged['x-required'] = !!existing['x-required'] || !!incoming['x-required'];
    merged['x-optional'] = requiresAllContexts ? !!optionalFlag : optionalFlag !== undefined ? optionalFlag : true;
    normalizeTypeBoxOptionalSymbol(merged, existing, incoming);

    return merged;
}

/**
 * Merges a single access context method (get/post/patch/put/remove).
 * Uses OR semantics: if either has access, merged result has access.
 * Preserves observedLocation from the schema that grants access.
 */
function mergeAccessContextMethod(existingMethod = {}, incomingMethod = {}) {
    const existingValue = !!existingMethod?.value;
    const incomingValue = !!incomingMethod?.value;
    const value = existingValue || incomingValue;

    // Prefer observedLocation from whichever schema grants access
    const observedLocation = existingValue
        ? existingMethod?.observedLocation
        : incomingValue
          ? incomingMethod?.observedLocation
          : (existingMethod?.observedLocation ?? incomingMethod?.observedLocation);

    return {
        value,
        ...(observedLocation ? { observedLocation } : {})
    };
}

/**
 * Merges x-accessContext objects with OR semantics.
 * Multiple UI paths accumulate write capabilities.
 * @returns {object|undefined} Merged context or undefined if both inputs empty
 */
function mergeAccessContexts(existingContext, incomingContext) {
    if (!existingContext && !incomingContext) return undefined;
    if (!existingContext) return incomingContext;
    if (!incomingContext) return existingContext;

    const merged = {};
    ['get', 'post', 'patch', 'put', 'remove'].forEach(method => {
        const existingMethod = existingContext?.[method];
        const incomingMethod = incomingContext?.[method];
        if (!existingMethod && !incomingMethod) return;
        merged[method] = mergeAccessContextMethod(existingMethod, incomingMethod);
    });
    return merged;
}

/**
 * Route hints use the same shape as x-accessContext, but they mean something
 * narrower: "this UI block needs this HTTP method exposed." When two blocks
 * target the same relation, keep every true method.
 */
function mergeRouteAccessHints(existingHints, incomingHints) {
    return mergeAccessContexts(existingHints, incomingHints);
}

/**
 * Merges partial type definitions (property schemas keyed by name).
 * @param {object} target - Existing type definitions
 * @param {object} incoming - New definitions to merge
 * @returns {object} Merged definitions with schema flags properly combined
 */
function mergePartialTypeDefinitions(target = {}, incoming = {}) {
    const result = { ...target };
    Object.entries(incoming || {}).forEach(([key, schema]) => {
        if (!result[key]) {
            result[key] = schema;
            return;
        }
        result[key] = mergeSchemaFlags(result[key], schema);
    });
    return result;
}

/**
 * Resolves field-level flags for UI rendering.
 * @param {boolean} required - Whether field is required
 * @param {object} context - Context with readOnly and forAction
 * @returns {object} Resolved flags for field rendering
 */
function resolveFieldFlags(required, context = {}) {
    const isRequired = !!required;
    const isEditable = !context?.readOnly;
    const forAction = context?.forAction; // 'create' or 'update' from DetailPane
    return {
        isRequired,
        isOptional: !isRequired,
        isEditable,
        isReadOnly: !isEditable,
        forAction
    };
}

/**
 * Merges two relation schemas with special handling for required arrays and action context.
 *
 * Key behaviors:
 * 1. Non-canonical actions (export, filter, list, nav, import) don't override canonical constraints
 * 2. Properties merged with x-forAction combination
 * 3. Required array only includes fields required for ALL action contexts
 * 4. anyOf/oneOf branches are combined
 *
 * @param {object} existing - Existing relation schema
 * @param {object} incoming - New schema to merge
 * @returns {object} Merged relation schema
 */
function mergeRelationSchemas(existing = {}, incoming = {}) {
    const nonCanonicalActions = ['export', 'filter', 'list', 'nav', 'import'];
    const incomingAction = incoming['x-forAction'];
    const existingAction = existing['x-forAction'];

    const incomingIsNonCanonical = nonCanonicalActions.includes(incomingAction);
    const existingIsCanonical = !existingAction || ['create', 'update'].includes(existingAction);

    // Non-canonical actions shouldn't override canonical required constraints
    if (incomingIsNonCanonical && existingIsCanonical && existing.required) {
        const merged = merge({}, existing, incoming);
        const accessContext = mergeAccessContexts(existing['x-accessContext'], incoming['x-accessContext']);
        if (accessContext) merged['x-accessContext'] = accessContext;
        const routeAccessHints = mergeRouteAccessHints(existing['x-routeAccessHints'], incoming['x-routeAccessHints']);
        if (routeAccessHints) merged['x-routeAccessHints'] = routeAccessHints;
        merged.required = existing.required;
        return merged;
    }

    const merged = merge({}, existing, incoming);

    const accessContext = mergeAccessContexts(existing['x-accessContext'], incoming['x-accessContext']);
    if (accessContext) merged['x-accessContext'] = accessContext;
    // lodash merge would let the later schema win. OR the route hints instead,
    // so create and update buttons on the same relation can both survive.
    const routeAccessHints = mergeRouteAccessHints(existing['x-routeAccessHints'], incoming['x-routeAccessHints']);
    if (routeAccessHints) merged['x-routeAccessHints'] = routeAccessHints;

    // Merge properties with proper x-forAction handling
    if (existing.properties || incoming.properties) {
        merged.properties = {};
        const allPropNames = new Set([
            ...Object.keys(existing.properties || {}),
            ...Object.keys(incoming.properties || {})
        ]);

        allPropNames.forEach(propName => {
            const existingProp = existing.properties?.[propName];
            const incomingProp = incoming.properties?.[propName];

            if (!existingProp) {
                merged.properties[propName] = incomingProp;
            } else if (!incomingProp) {
                merged.properties[propName] = existingProp;
            } else {
                merged.properties[propName] = mergeSchemaFlags(existingProp, incomingProp);
            }
        });
    }

    // Recompute required array from merged property metadata.
    // Keep legacy required[] candidates as conservative fallback if schema metadata is missing.
    const mergedProperties = merged.properties || {};
    const requiredCandidates = new Set([
        ...Object.keys(mergedProperties),
        ...(Array.isArray(existing.required) ? existing.required : []),
        ...(Array.isArray(incoming.required) ? incoming.required : [])
    ]);

    const requiredForAll = Array.from(requiredCandidates).filter(fieldName => {
        const prop = mergedProperties[fieldName];
        if (!prop) {
            // Fallback for legacy definitions that still rely only on required[] arrays.
            return (
                (Array.isArray(existing.required) && existing.required.includes(fieldName)) ||
                (Array.isArray(incoming.required) && incoming.required.includes(fieldName))
            );
        }
        return isRequiredForAllCanonicalContexts(fieldName, prop);
    });

    if (requiredForAll.length) {
        merged.required = uniq(requiredForAll).sort();
    } else {
        delete merged.required;
    }

    // Combine anyOf/oneOf branches from both schemas
    if (existing.anyOf || incoming.anyOf) {
        const existingBranches = existing.anyOf || (existing.type ? [existing] : []);
        const incomingBranches = incoming.anyOf || (incoming.type ? [incoming] : []);
        if (existingBranches.length && incomingBranches.length) {
            merged.anyOf = [...existingBranches, ...incomingBranches];
            delete merged.type;
            delete merged.properties;
            delete merged.required;
        }
    }

    return merged;
}

/**
 * Merges two subDataDictionary objects (namespace -> relation -> schema).
 * Uses mergeRelationSchemas for relation-level merging.
 *
 * @param {object} target - Existing dictionary {namespace: {relation: schema}}
 * @param {object} source - Dictionary to merge in
 * @returns {object} Merged dictionary
 */
function mergeSubDataDictionaries(target = {}, source = {}) {
    const result = { ...target };

    Object.entries(source).forEach(([namespace, relations]) => {
        if (!result[namespace]) {
            result[namespace] = { ...relations };
            return;
        }

        Object.entries(relations || {}).forEach(([relation, schema]) => {
            if (!result[namespace][relation]) {
                result[namespace][relation] = schema;
            } else {
                result[namespace][relation] = mergeRelationSchemas(
                    result[namespace][relation],
                    schema,
                    `${namespace}.${relation}`
                );
            }
        });
    });

    return result;
}

module.exports = {
    registerHNodeType,
    getHNodeTypeGroup,
    getHNodeType,
    resolveFieldFlags,
    mergePartialTypeDefinitions,
    mergeRelationSchemas,
    mergeSubDataDictionaries
};
